import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign up function
  const signup = async (email, password, firstName, lastName) => {
    try {
      setError(null);
      setLoading(true);
      
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update user profile with display name
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`
      });
      
      // Save comprehensive user data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: email.toLowerCase(),
        firstName: firstName,
        lastName: lastName,
        displayName: `${firstName} ${lastName}`,
        phone: '',
        dateOfBirth: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: ''
        },
        preferences: {
          emailNotifications: true,
          smsNotifications: false,
          marketingCommunications: true
        },
        profile: {
          isComplete: false,
          lastUpdated: new Date().toISOString()
        },
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      console.log('✅ User data successfully saved to Firestore:', {
        uid: user.uid,
        email: email,
        name: `${firstName} ${lastName}`
      });
      
      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign in function
  const signin = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update last login time and ensure data consistency
      await setDoc(doc(db, 'users', user.uid), {
        lastLogin: new Date().toISOString(),
        email: user.email.toLowerCase(), // Ensure email consistency
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      console.log('✅ User login data updated in Firestore:', {
        uid: user.uid,
        email: user.email,
        loginTime: new Date().toISOString()
      });
      
      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Get user data from Firestore
  const getUserData = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        console.log("No user data found!");
        return null;
      }
    } catch (error) {
      console.error("Error getting user data:", error);
      return null;
    }
  };

  // Monitor auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in - preserve the Firebase Auth user object
        const userData = await getUserData(user.uid);
        // Create a user object that preserves Firebase Auth methods
        const enhancedUser = Object.assign(user, {
          // Add Firestore data as additional properties
          firestoreData: userData || {},
          // Preserve specific Firestore fields for easy access
          firstName: userData?.firstName || '',
          lastName: userData?.lastName || '',
          phone: userData?.phone || '',
          address: userData?.address || {},
          preferences: userData?.preferences || {}
        });
        setCurrentUser(enhancedUser);
      } else {
        // User is signed out
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    signin,
    logout,
    getUserData,
    loading,
    error,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
